import BackofficeLayout from '@/components/backoffice/BackofficeLayout';
import { useBackofficeAuth } from '@/contexts/BackofficeAuthContext';
import { useNotificacoes } from '@/hooks/useNotificacoes';
import { chamadosSuporte } from '@/mocks/backoffice/chamados';
import { operacoesPendentes } from '@/mocks/backoffice/operacoes';
import { empresasPlataforma } from '@/mocks/backoffice/empresas';
import { Link, useNavigate } from 'react-router-dom';

export default function BackofficeDashboardPage() {
  const { session, role } = useBackofficeAuth();
  const navigate = useNavigate();
  const { criticos, marcarComoLida, notificacoes } = useNotificacoes();

  // KPIs
  const totalEmpresas = empresasPlataforma.length;
  const empresasAtivas = empresasPlataforma.filter(e => e.status === 'ativa').length;
  const empresasPendentes = empresasPlataforma.filter(e => e.status === 'pendente').length;
  const totalParticipantes = empresasPlataforma.reduce((sum, e) => sum + e.participantes_ativos, 0);
  const totalParticipantesEmAnalise = empresasPlataforma.reduce((sum, e) => sum + e.participantes_em_analise, 0);
  const chamadosAbertos = chamadosSuporte.filter(c => c.status === 'aberto').length;
  const chamadosEmAndamento = chamadosSuporte.filter(c => c.status === 'em_andamento').length;
  const chamadosUrgentes = chamadosSuporte.filter(c => c.prioridade === 'urgente' && c.status !== 'resolvido' && c.status !== 'fechado').length;
  const opsPendentes = operacoesPendentes.filter(o => o.status === 'pendente').length;
  const opsValorPendente = operacoesPendentes
    .filter(o => o.status === 'pendente')
    .reduce((sum, o) => sum + o.valor, 0);

  const formatCurrency = (val: number) =>
    val.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

  const formatNumber = (val: number) =>
    val.toLocaleString('pt-BR');

  // Status badge helper
  const statusConfig: Record<string, { bg: string; text: string; label: string }> = {
    ativa: { bg: 'bg-emerald-50', text: 'text-emerald-600', label: 'Ativa' },
    pendente: { bg: 'bg-amber-50', text: 'text-amber-600', label: 'Pendente' },
    suspensa: { bg: 'bg-orange-50', text: 'text-orange-600', label: 'Suspensa' },
    inativa: { bg: 'bg-gray-50', text: 'text-gray-500', label: 'Inativa' },
  };

  const chamadoStatusConfig: Record<string, { bg: string; text: string; label: string }> = {
    aberto: { bg: 'bg-red-50', text: 'text-red-600', label: 'Aberto' },
    em_andamento: { bg: 'bg-blue-50', text: 'text-blue-600', label: 'Em andamento' },
    aguardando: { bg: 'bg-amber-50', text: 'text-amber-600', label: 'Aguardando' },
    resolvido: { bg: 'bg-emerald-50', text: 'text-emerald-600', label: 'Resolvido' },
    fechado: { bg: 'bg-gray-50', text: 'text-gray-500', label: 'Fechado' },
  };

  const prioridadeConfig: Record<string, { bg: string; text: string }> = {
    baixa: { bg: 'bg-gray-100', text: 'text-gray-600' },
    media: { bg: 'bg-blue-50', text: 'text-blue-600' },
    alta: { bg: 'bg-orange-50', text: 'text-orange-600' },
    urgente: { bg: 'bg-red-50', text: 'text-red-600' },
  };

  const handleCriticoClick = (notif: (typeof criticos)[0]) => {
    marcarComoLida(notif.id);
    navigate(notif.link);
  };

  // Urgentes: chamados + empresas pendentes
  const alertas = [
    ...chamadosSuporte
      .filter(c => c.prioridade === 'urgente' && c.status !== 'resolvido' && c.status !== 'fechado')
      .map(c => ({ tipo: 'chamado' as const, item: c })),
    ...empresasPlataforma
      .filter(e => e.status === 'pendente')
      .map(e => ({ tipo: 'empresa' as const, item: e })),
  ].slice(0, 5);

  return (
    <BackofficeLayout>
      <div className="space-y-8">
        {/* Welcome */}
        <div>
          <h1 className="text-2xl font-bold text-gray-800">
            Olá, {session.nome?.split(' ')[0] || 'Admin'}!
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Aqui está o panorama geral da plataforma hoje.
          </p>
        </div>

        {/* Banner de Notificações Críticas */}
        {criticos.length > 0 && (
          <div className="space-y-2">
            <div className="flex items-center space-x-2 mb-1">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-error-warning-line text-red-500 text-sm" />
              </div>
              <h3 className="text-xs font-bold text-red-700 uppercase tracking-wider">
                Ações Críticas Requerem Atenção ({criticos.length})
              </h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {criticos.map((n) => {
                const badgeBg =
                  n.modulo === 'financeiro'
                    ? 'bg-red-50 border-red-200 hover:bg-red-50'
                    : n.modulo === 'atendimento'
                    ? 'bg-red-50 border-red-200 hover:bg-red-50'
                    : n.modulo === 'empresas'
                    ? 'bg-red-50 border-red-200 hover:bg-red-50'
                    : 'bg-red-50 border-red-200 hover:bg-red-50';
                return (
                  <button
                    key={n.id}
                    onClick={() => handleCriticoClick(n)}
                    className={`text-left p-4 rounded-xl border transition-all cursor-pointer ${badgeBg}`}
                  >
                    <div className="flex items-start space-x-3">
                      <div className="w-8 h-8 rounded-lg bg-red-100 flex items-center justify-center flex-shrink-0">
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-error-warning-line text-red-600 text-sm" />
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-bold text-red-800 truncate">
                          {n.titulo}
                        </p>
                        <p className="text-[11px] text-red-600/80 mt-1 leading-relaxed line-clamp-2">
                          {n.descricao}
                        </p>
                        <div className="flex items-center space-x-2 mt-2">
                          <span className="px-2 py-0.5 rounded bg-red-200 text-red-700 text-[10px] font-bold uppercase">
                            {n.modulo}
                          </span>
                          <span className="text-[10px] text-red-500">
                            {new Date(n.data).toLocaleDateString('pt-BR', {
                              day: '2-digit',
                              month: '2-digit',
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </span>
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Alertas Urgentes legado (chamados + empresas pendentes) */}
        {alertas.length > 0 && (
          <div className="bg-red-50 border border-red-100 rounded-xl p-4">
            <div className="flex items-center space-x-2 mb-3">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-alarm-warning-line text-red-500 text-sm" />
              </div>
              <h3 className="text-xs font-bold text-red-700 uppercase tracking-wider">
                Atenção Imediata ({alertas.length})
              </h3>
            </div>
            <div className="flex flex-wrap gap-2">
              {alertas.map((alerta, idx) => {
                if (alerta.tipo === 'chamado') {
                  const c = alerta.item as (typeof chamadosSuporte)[0];
                  return (
                    <button
                      key={idx}
                      onClick={() => navigate(`/backoffice/atendimento/${c.id}`)}
                      className="flex items-center space-x-2 px-3 py-2 bg-white border border-red-200 rounded-lg hover:bg-red-50 transition-all cursor-pointer"
                    >
                      <i className="ri-customer-service-line text-red-500 text-xs" />
                      <span className="text-xs font-bold text-gray-800">
                        {c.id}
                      </span>
                      <span className="text-[10px] text-gray-500 truncate max-w-[140px]">
                        {c.titulo}
                      </span>
                    </button>
                  );
                }
                const e = alerta.item as (typeof empresasPlataforma)[0];
                return (
                  <button
                    key={idx}
                    onClick={() => navigate(`/backoffice/empresas/${e.id}`)}
                    className="flex items-center space-x-2 px-3 py-2 bg-white border border-amber-200 rounded-lg hover:bg-amber-50 transition-all cursor-pointer"
                  >
                    <i className="ri-building-line text-amber-500 text-xs" />
                    <span className="text-xs font-bold text-gray-800">
                      {e.nome_fantasia}
                    </span>
                    <span className="text-[10px] text-gray-500">
                      cadastro pendente
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* KPI Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Empresas */}
          <button
            onClick={() => navigate('/backoffice/empresas')}
            className="bg-white rounded-xl p-5 border border-gray-100 hover:border-emerald-200 hover:shadow-sm transition-all text-left cursor-pointer"
          >
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">
                  Empresas
                </p>
                <p className="text-2xl font-bold text-gray-800 mt-1">
                  {totalEmpresas}
                </p>
                <p className="text-xs text-emerald-600 mt-1 font-medium">
                  {empresasAtivas} ativas
                </p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-building-2-line text-emerald-600 text-lg" />
                </div>
              </div>
            </div>
            {empresasPendentes > 0 && (
              <div className="mt-3 pt-3 border-t border-gray-50 flex items-center space-x-1.5">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-error-warning-line text-amber-500 text-xs" />
                </div>
                <span className="text-[10px] text-amber-600 font-bold">
                  {empresasPendentes} aguardando aprovação
                </span>
              </div>
            )}
          </button>

          {/* Participantes */}
          <div className="bg-white rounded-xl p-5 border border-gray-100">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">
                  Participantes
                </p>
                <p className="text-2xl font-bold text-gray-800 mt-1">
                  {formatNumber(totalParticipantes)}
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  Ativos na plataforma
                </p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-user-star-line text-blue-600 text-lg" />
                </div>
              </div>
            </div>
            {totalParticipantesEmAnalise > 0 && (
              <div className="mt-3 pt-3 border-t border-gray-50 flex items-center space-x-1.5">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-time-line text-amber-500 text-xs" />
                </div>
                <span className="text-[10px] text-amber-600 font-bold">
                  {formatNumber(totalParticipantesEmAnalise)} em análise
                </span>
              </div>
            )}
          </div>

          {/* Chamados */}
          <button
            onClick={() => navigate('/backoffice/atendimento')}
            className="bg-white rounded-xl p-5 border border-gray-100 hover:border-amber-200 hover:shadow-sm transition-all text-left cursor-pointer"
          >
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">
                  Chamados
                </p>
                <p className="text-2xl font-bold text-gray-800 mt-1">
                  {chamadosAbertos + chamadosEmAndamento}
                </p>
                {chamadosUrgentes > 0 && (
                  <p className="text-xs text-red-600 mt-1 font-medium">
                    {chamadosUrgentes} urgentes
                  </p>
                )}
                {chamadosUrgentes === 0 && (
                  <p className="text-xs text-gray-400 mt-1">
                    {chamadosAbertos} abertos
                  </p>
                )}
              </div>
              <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-customer-service-line text-amber-600 text-lg" />
                </div>
              </div>
            </div>
          </button>

          {/* Operações Pendentes */}
          <button
            onClick={() => navigate('/backoffice/financeiro')}
            className="bg-white rounded-xl p-5 border border-gray-100 hover:border-red-200 hover:shadow-sm transition-all text-left cursor-pointer"
          >
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">
                  Financeiro
                </p>
                <p className="text-2xl font-bold text-gray-800 mt-1">
                  {opsPendentes}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {formatCurrency(opsValorPendente)} pendentes
                </p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-red-50 flex items-center justify-center">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-time-line text-red-600 text-lg" />
                </div>
              </div>
            </div>
          </button>
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Empresas por Status */}
          <div className="bg-white rounded-xl p-6 border border-gray-100">
            <h3 className="text-sm font-bold text-gray-800 mb-4">
              Empresas por Status
            </h3>
            <div className="space-y-3">
              {Object.entries(
                empresasPlataforma.reduce((acc, e) => {
                  acc[e.status] = (acc[e.status] || 0) + 1;
                  return acc;
                }, {} as Record<string, number>)
              ).map(([status, count]) => {
                const cfg = statusConfig[status] || statusConfig.inativa;
                const pct = (count / totalEmpresas) * 100;
                return (
                  <div key={status}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-medium text-gray-600">
                        {cfg.label}
                      </span>
                      <span className="text-xs font-bold text-gray-800">
                        {count} ({Math.round(pct)}%)
                      </span>
                    </div>
                    <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full ${
                          status === 'ativa'
                            ? 'bg-emerald-500'
                            : status === 'pendente'
                            ? 'bg-amber-500'
                            : status === 'suspensa'
                            ? 'bg-orange-500'
                            : 'bg-gray-400'
                        }`}
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Faturamento por Plano */}
          <div className="bg-white rounded-xl p-6 border border-gray-100">
            <h3 className="text-sm font-bold text-gray-800 mb-4">
              Faturamento Mensal por Plano
            </h3>
            <div className="space-y-3">
              {(['basico', 'profissional', 'enterprise'] as const).map(
                (plano) => {
                  const empresasNoPlano = empresasPlataforma.filter(
                    (e) => e.plano === plano && e.status === 'ativa'
                  );
                  const faturamento = empresasNoPlano.reduce(
                    (sum, e) => sum + e.faturamento_mensal,
                    0
                  );
                  const totalFat = empresasPlataforma
                    .filter((e) => e.status === 'ativa')
                    .reduce((sum, e) => sum + e.faturamento_mensal, 0);
                  const pct =
                    totalFat > 0 ? (faturamento / totalFat) * 100 : 0;
                  const label =
                    plano === 'basico'
                      ? 'Básico'
                      : plano === 'profissional'
                      ? 'Profissional'
                      : 'Enterprise';
                  return (
                    <div key={plano}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-medium text-gray-600">
                          {label}
                        </span>
                        <span className="text-xs font-bold text-gray-800">
                          {formatCurrency(faturamento)}
                        </span>
                      </div>
                      <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full ${
                            plano === 'basico'
                              ? 'bg-sky-500'
                              : plano === 'profissional'
                              ? 'bg-emerald-500'
                              : 'bg-violet-500'
                          }`}
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </div>
                  );
                }
              )}
            </div>
            <div className="mt-4 pt-3 border-t border-gray-100 flex justify-between">
              <span className="text-xs text-gray-500">Total mensal</span>
              <span className="text-sm font-bold text-gray-800">
                {formatCurrency(
                  empresasPlataforma
                    .filter((e) => e.status === 'ativa')
                    .reduce((sum, e) => sum + e.faturamento_mensal, 0)
                )}
              </span>
            </div>
          </div>
        </div>

        {/* Tables Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Últimos Chamados */}
          <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
            <div className="p-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-sm font-bold text-gray-800">
                Últimos Chamados
              </h3>
              <Link
                to="/backoffice/atendimento"
                className="text-xs font-bold text-emerald-600 hover:text-emerald-700 transition-colors cursor-pointer"
              >
                Ver todos
              </Link>
            </div>
            <div className="divide-y divide-gray-50">
              {chamadosSuporte.slice(0, 5).map((chamado) => {
                const st =
                  chamadoStatusConfig[chamado.status] ||
                  chamadoStatusConfig.aberto;
                const pr =
                  prioridadeConfig[chamado.prioridade] ||
                  prioridadeConfig.baixa;
                return (
                  <button
                    key={chamado.id}
                    onClick={() =>
                      navigate(`/backoffice/atendimento/${chamado.id}`)
                    }
                    className="w-full p-4 hover:bg-gray-50 transition-colors text-left cursor-pointer"
                  >
                    <div className="flex items-start justify-between">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center space-x-2 mb-1">
                          <span
                            className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${st.bg} ${st.text}`}
                          >
                            {st.label}
                          </span>
                          <span
                            className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${pr.bg} ${pr.text}`}
                          >
                            {chamado.prioridade}
                          </span>
                        </div>
                        <p className="text-xs font-bold text-gray-800 truncate">
                          {chamado.titulo}
                        </p>
                        <p className="text-[10px] text-gray-400 mt-0.5">
                          {chamado.usuario_nome} ·{' '}
                          {chamado.usuario_tipo === 'empresa'
                            ? 'Empresa'
                            : 'Participante'}
                        </p>
                      </div>
                      <span className="text-[10px] text-gray-400 flex-shrink-0 ml-2">
                        {new Date(chamado.data_criacao).toLocaleDateString(
                          'pt-BR'
                        )}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Operações Pendentes */}
          <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
            <div className="p-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-sm font-bold text-gray-800">
                Operações Pendentes
              </h3>
              <Link
                to="/backoffice/financeiro"
                className="text-xs font-bold text-emerald-600 hover:text-emerald-700 transition-colors cursor-pointer"
              >
                Ver todas
              </Link>
            </div>
            <div className="divide-y divide-gray-50">
              {operacoesPendentes
                .filter((o) => o.status === 'pendente')
                .slice(0, 5)
                .map((op) => {
                  const tipoLabel: Record<string, string> = {
                    saque_pix: 'Saque PIX',
                    credito_pontos: 'Compra de Pontos',
                    debito_pontos: 'Débito',
                    ajuste_saldo: 'Ajuste',
                    estorno: 'Estorno',
                    compra_pontos: 'Compra Pontos',
                  };
                  const tipoColors: Record<string, { bg: string; text: string }> =
                    {
                      saque_pix: { bg: 'bg-emerald-50', text: 'text-emerald-600' },
                      credito_pontos: { bg: 'bg-blue-50', text: 'text-blue-600' },
                      debito_pontos: { bg: 'bg-red-50', text: 'text-red-600' },
                      ajuste_saldo: {
                        bg: 'bg-amber-50',
                        text: 'text-amber-600',
                      },
                      estorno: {
                        bg: 'bg-orange-50',
                        text: 'text-orange-600',
                      },
                      compra_pontos: { bg: 'bg-blue-50', text: 'text-blue-600' },
                    };
                  const tc = tipoColors[op.tipo] || tipoColors.saque_pix;
                  return (
                    <button
                      key={op.id}
                      onClick={() => navigate('/backoffice/financeiro')}
                      className="w-full p-4 hover:bg-gray-50 transition-colors text-left cursor-pointer"
                    >
                      <div className="flex items-start justify-between">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center space-x-2 mb-1">
                            <span
                              className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${tc.bg} ${tc.text}`}
                            >
                              {tipoLabel[op.tipo] || op.tipo}
                            </span>
                          </div>
                          <p className="text-xs font-bold text-gray-800">
                            {op.usuario_nome}
                          </p>
                          <p className="text-[10px] text-gray-400 mt-0.5 truncate">
                            {op.descricao}
                          </p>
                        </div>
                        <div className="text-right flex-shrink-0 ml-2">
                          <p className="text-sm font-bold text-gray-800">
                            {op.valor_formatado}
                          </p>
                          <span className="text-[10px] text-gray-400">
                            {new Date(
                              op.data_solicitacao
                            ).toLocaleDateString('pt-BR')}
                          </span>
                        </div>
                      </div>
                    </button>
                  );
                })}
            </div>
            {operacoesPendentes.filter((o) => o.status === 'pendente')
              .length === 0 && (
              <div className="p-8 text-center">
                <div className="w-10 h-10 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-2">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-check-line text-emerald-500" />
                  </div>
                </div>
                <p className="text-xs text-gray-400">
                  Nenhuma operação pendente
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Empresas Cadastradas */}
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <div className="p-4 border-b border-gray-100 flex items-center justify-between">
            <h3 className="text-sm font-bold text-gray-800">
              Empresas Cadastradas
            </h3>
            <Link
              to="/backoffice/empresas"
              className="text-xs font-bold text-emerald-600 hover:text-emerald-700 transition-colors cursor-pointer"
            >
              Ver todas
            </Link>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50">
                  <th className="text-left px-4 py-3 text-[10px] font-bold text-gray-500 uppercase tracking-wider">
                    Empresa
                  </th>
                  <th className="text-left px-4 py-3 text-[10px] font-bold text-gray-500 uppercase tracking-wider">
                    Plano
                  </th>
                  <th className="text-left px-4 py-3 text-[10px] font-bold text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="text-right px-4 py-3 text-[10px] font-bold text-gray-500 uppercase tracking-wider">
                    Participantes
                  </th>
                  <th className="text-right px-4 py-3 text-[10px] font-bold text-gray-500 uppercase tracking-wider">
                    Faturamento
                  </th>
                  <th className="text-left px-4 py-3 text-[10px] font-bold text-gray-500 uppercase tracking-wider">
                    Responsável
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {empresasPlataforma.map((empresa) => {
                  const st =
                    statusConfig[empresa.status] || statusConfig.inativa;
                  return (
                    <tr
                      key={empresa.id}
                      onClick={() =>
                        navigate(`/backoffice/empresas/${empresa.id}`)
                      }
                      className="hover:bg-gray-50 transition-colors cursor-pointer"
                    >
                      <td className="px-4 py-3">
                        <div>
                          <p className="text-xs font-bold text-gray-800">
                            {empresa.nome_fantasia}
                          </p>
                          <p className="text-[10px] text-gray-400">
                            {empresa.cnpj}
                          </p>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs text-gray-600 capitalize">
                          {empresa.plano}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex px-2 py-0.5 rounded text-[10px] font-bold uppercase ${st.bg} ${st.text}`}
                        >
                          {st.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className="text-xs font-bold text-gray-800">
                          {formatNumber(empresa.participantes_ativos)}
                        </span>
                        <span className="text-[10px] text-gray-400">
                          {' '}
                          / {formatNumber(empresa.participantes_total)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className="text-xs font-bold text-gray-800">
                          {formatCurrency(empresa.faturamento_mensal)}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div>
                          <p className="text-xs text-gray-700">
                            {empresa.responsavel_nome}
                          </p>
                          <p className="text-[10px] text-gray-400">
                            {empresa.responsavel_email}
                          </p>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </BackofficeLayout>
  );
}